import { ArrowDropDownOutlined, ArrowRightOutlined } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { DateComponent, DateVariant } from "components/base/DateComponent";
import { CustomTooltip } from "components/disw";
import Table, { TableColumn } from "components/disw/Table";
import { orderBy } from "lodash";
import React from "react";
import warningStatus from "./icons/warningStatus.svg";
import { NotificationDeliveryStatus } from "./types";

const useStyles = makeStyles({
  root: {
    boxShadow: "none",
    marginBottom: 16,
    "&::before": {
      background: "transparent"
    }
  },
  summary: {
    backgroundColor: "#F0F0F0 !important",
    padding: "4px 8px !important",
    minHeight: "32px !important",
    height: "32px !important",
    "&:hover": {
      backgroundColor: "#CDE6EB !important"
    },
    "&:focus": {
      border: "2px solid var(--Border-focus-light, #0F789B)",
      backgroundColor: "#F0F0F0 !important"
    }
  },
  arrowIcon: {
    color: "#464646",
    padding: "4px 0px"
  },
  summaryText: {
    fontFamily: "Segoe UI !important",
    fontWeight: "600 !important",
    fontStyle: "normal",
    fontSize: "12px !important",
    lineHeight: "16px !important",
    letterSpacing: 0,
    color: "#1E1E1E",
    borderRadius: 3,
    padding: "4px 0px"
  },
  headerContent: {
    margin: 0
  },
  expanded: {
    minHeight: "32px !important",
    maxHeight: "32px !important",
    height: "32px !important"
  },
  details: {
    padding: "16px !important"
  },
  warningIcon: {
    width: 16,
    height: 16
  },
  summaryRow: {
    display: "flex",
    alignItems: "center",
    width: "100%"
  },
  summaryLeft: {
    display: "flex",
    alignItems: "center",
    flex: 1
  },
  emptyLog: {
    width: "100%",
    textAlign: "center",
    padding: 16,
    color: "#1E1E1E"
  }
});

interface ServiceProductNotificationStatusProps {
  data: NotificationDeliveryStatus[];
}

const ServiceProductNotificationStatus: React.FC<
  ServiceProductNotificationStatusProps
> = ({ data }) => {
  const classes = useStyles();
  const [expanded, setExpanded] = React.useState(false);

  const sortedData = orderBy(
    data,
    ["email", "period", "threshold"],
    ["asc", "asc", "asc"]
  );

  const errorRows = sortedData.filter(
    row => row.error && row.error.trim() !== ""
  );
  const errorCount = errorRows.length;
  const showErrorColumn = errorCount > 0;
  const dynamicColumns: TableColumn<NotificationDeliveryStatus>[] = [
    { key: "email", header: "E-Mail" },
    {
      key: "periodNumber",
      header: "Period"
    },
    {
      key: "thresholdValue",
      header: "Threshold",
      render: row => `${Math.round(row.thresholdValue * 100)}%`
    },
    { key: "deliveryStatus", header: "Status" },
    {
      key: "triggeredDate",
      header: "Date Triggered",
      render: row => (
        <DateComponent
          date={row.triggeredDate}
          variant={DateVariant.SHORT_WITH_TIME}
        />
      )
    },
    ...(showErrorColumn
      ? [{ key: "error" as keyof NotificationDeliveryStatus, header: "Error" }]
      : [])
  ];

  return (
    <Accordion
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
      className={classes.root}
    >
      <AccordionSummary
        classes={{
          root: classes.summary,
          content: classes.headerContent,
          expanded: classes.expanded
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%"
          }}
        >
          <span
            style={{
              display: "flex",
              alignItems: "center"
            }}
          >
            {expanded ? (
              <ArrowDropDownOutlined className={classes.arrowIcon} />
            ) : (
              <ArrowRightOutlined className={classes.arrowIcon} />
            )}
            <Typography className={classes.summaryText}>
              Notification delivery log
            </Typography>
          </span>
          {errorCount > 0 && (
            <CustomTooltip
              title={`${errorCount} E-Mail${
                errorCount > 1 ? "s" : ""
              } did not deliver successfully`}
              arrow
              placement="top"
            >
              <img
                src={warningStatus}
                alt="Warning status"
                className={classes.warningIcon}
              />
            </CustomTooltip>
          )}
        </div>
      </AccordionSummary>
      <AccordionDetails classes={{ root: classes.details }}>
        {sortedData.length > 0 ? (
          <Table
            columns={dynamicColumns}
            data={sortedData}
            rowKey={(_, idx) => idx}
          />
        ) : (
          <Typography color="textSecondary" className={classes.emptyLog}>
            No notifications have been sent.
          </Typography>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default ServiceProductNotificationStatus;
